import React, { useState, useEffect, useMemo, useRef } from 'react';
import window from 'window-or-global';

import S from './SlideToUnlock.module.css';

interface State {
  x: number | null;
  delta: number | null;
}

const maxWith = 202; // TODO: detect automatically based on DOM elements width
const unlockGap = 50;

const getX = (event) =>
  event.touches && event.touches[0] ? event.touches[0].clientX : event.clientX;

const SlideToUnlock = ({ isLocked, onUnlock }) => {
  const initialState: State = {
    x: null,
    delta: null
  };
  const [state, setState] = useState(initialState);

  const slider: React.RefObject<HTMLDivElement> = useRef(null);

  const { x, delta } = state;

  const onMouseMove = useMemo(
    () => (event) => {
      if (x === null) {
        return;
      }
      const clientX = getX(event) || 0;
      setState((prevState) => {
        const delta = clientX - (prevState.x || 0);
        const safeDelta = delta < 0 ? 0 : delta > maxWith ? maxWith : delta;
        return { ...prevState, delta: safeDelta };
      });
    },
    [x]
  );

  const startUnlockMove = useMemo(
    () => (event) => {
      event.preventDefault();
      setState((prevState) => ({ ...prevState, x: getX(event) }));
    },
    [slider]
  );

  const stopUnlockMove = useMemo(
    () => () => {
      setState((prevState) => ({ ...prevState, x: null, delta: null }));
      if (maxWith - (delta || 0) < unlockGap) {
        onUnlock();
      }
    },
    [delta]
  );

  useEffect(() => {
    const $el = slider.current;
    if (!$el) {
      return;
    }
    $el.addEventListener('mousedown', startUnlockMove);
    window.document.addEventListener('mouseup', stopUnlockMove);
    window.document.addEventListener('mousemove', onMouseMove);

    // mobile events
    $el.addEventListener('touchstart', startUnlockMove);
    window.document.addEventListener('touchend', stopUnlockMove);
    window.document.addEventListener('touchmove', onMouseMove);

    return () => {
      if (!$el) {
        return;
      }
      $el.removeEventListener('mousedown', startUnlockMove);
      window.document.removeEventListener('mouseup', stopUnlockMove);
      window.document.removeEventListener('mousemove', onMouseMove);

      // mobile events
      $el.removeEventListener('touchstart', startUnlockMove);
      window.document.removeEventListener('touchend', stopUnlockMove);
      window.document.removeEventListener('touchmove', onMouseMove);
    };
  }, [onMouseMove, startUnlockMove, stopUnlockMove, slider.current]);

  const calcOpacity = 1 - (delta || 0) / 100;
  const opacity = calcOpacity < 0 ? 0 : calcOpacity;
  const left = delta || 0;

  return (
    <div className={S.root} data-is-locked={isLocked.toString()}>
      <div className={S.unlock}>
        <div
          className={S.slider}
          style={{ left, transition: left ? undefined : 'left .2s' }}
          ref={slider}
        />
        <div
          className={S.text}
          style={{ opacity, transition: left ? undefined : 'opacity .2s' }}
        >
          slide to unlock
        </div>
      </div>
    </div>
  );
};

export default SlideToUnlock;
